import { Fragment, useEffect, useRef, useState } from 'react';
import { PageHead } from 'ui/kit/PageHead';

import { Button } from 'ui';
import { Pencil } from 'ui/icons/Pencil';

import { useTaskFormStore } from '../NewTask/taskForm.store';
import { getVisibleSteps } from '../NewTask/taskForm.steps';
import { TaskWizardModal } from '../NewTask/TaskWizardModal';
import { formatValue, mapTaskFormToRequest, taskValidationSchema } from '../NewTask/comfig';
import { useNavigate } from 'react-router';
import { useAddBulletin } from 'hooks/useAddBulletin';
import { useGetCurrentProfile } from 'modules/Profile/model/services';
import { METRIKA_GOALS, reachMetrikaGoal } from 'utils/metrikaGoals';
import { createStudentProposal } from 'api/v1/requests/bulletins';
import { addNotification } from 'notifications';
import { buildChatUrl } from 'utils/buildChatUrl';
import screen from '../StudentTaskScreen.module.scss';

export const DetailsTask = () => {
  const { values, reset } = useTaskFormStore();
  const { data: profile } = useGetCurrentProfile();

  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const navigate = useNavigate();

  const addBulletin = useAddBulletin();

  const profileContext = {
    registrant_type: (profile?.registrant_type as 'self' | 'parent' | undefined) || 'self',
  };
  const steps = getVisibleSteps(values, profileContext);
  const stepsKey = steps.map((step) => step.key).join('|');

  // Обязательные шаги, которые в черновике не заполнены. Сюда можно попасть по прямой
  // ссылке или кнопке «Создать задачу» (пустое состояние чатов) со старым или пустым
  // черновиком: тогда страница сама становится мастером с шага 1 (карточка в колонке,
  // как в Figma 55:35441), а не модалкой поверх проверки. Ошибки на карточке проверки
  // показываем только после попытки сохранить.
  const [missingSteps, setMissingSteps] = useState<string[] | null>(null);
  const [wizardInline, setWizardInline] = useState<boolean | null>(null);
  const [saveAttempted, setSaveAttempted] = useState(false);
  // Ответ сервера при отказе (например, правила текста в имени или пожеланиях):
  // раньше ошибка уходила только в консоль, и кнопка просто ничего не делала.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const decidedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    const collect = (paths: Set<string>) => {
      if (!values.studentType) paths.add('studentType');
      return steps
        .filter((step) => step.fields.some((field) => paths.has(field)))
        .map((step) => step.key);
    };
    taskValidationSchema
      .validate(values, { abortEarly: false })
      .then(() => {
        if (alive) setMissingSteps(collect(new Set()));
      })
      .catch((error: { inner?: Array<{ path?: string }>; path?: string }) => {
        if (!alive) return;
        const paths = new Set<string>();
        (error?.inner || []).forEach((item) => item.path && paths.add(item.path));
        if (error?.path) paths.add(error.path);
        setMissingSteps(collect(paths));
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, stepsKey]);

  const incomplete = (missingSteps?.length ?? 0) > 0;
  const missingTitles = steps
    .filter((step) => missingSteps?.includes(step.key))
    .map((step) => step.title.toLowerCase());

  const openModal = (index: number) => {
    setCurrentIndex(index);
    setModalOpen(true);
  };

  useEffect(() => {
    if (missingSteps === null || decidedRef.current) return;
    decidedRef.current = true;
    setWizardInline(missingSteps.length > 0);
  }, [missingSteps]);

  const showErrors = saveAttempted && incomplete;

  if (wizardInline === null) {
    return <main className={screen.page} aria-busy='true' />;
  }

  if (wizardInline) {
    return (
      <main className={screen.page}>
        <TaskWizardModal
          inline
          open
          initialStep={0}
          onClose={() => navigate('/profile/tasks')}
          onFinish={() => {
            setSaveAttempted(false);
            setWizardInline(false);
            window.scrollTo({ top: 0 });
          }}
        />
      </main>
    );
  }

  return (
    <main className={screen.page}>
      <PageHead crumb={<>Личный кабинет / Мои задачи</>} title={<>Задача готова</>} />
      <section className={screen.card}>
        {/* «Шаг N из N» - внутри карточки, как в мастере (раньше висел над ней). */}
        <p className={screen.mute}>
          Шаг {Math.max(steps.length, 1)} из {Math.max(steps.length, 1)}
        </p>
        <h2 className={screen.cardTitle}>Проверьте условия задачи</h2>
        <p className={screen.mute}>
          Проверь условия. Задача появится у репетиторов после сохранения.
        </p>

        {steps.map((step: { key: string; title: string }, i: number) => (
          <Fragment key={step.key}>
            <button type='button' className={screen.field} onClick={() => openModal(i)}>
              <p className={screen.fieldLabel}>{step.title}</p>
              <p className={screen.fieldValue}>
                {formatValue(step.key, values) || 'Не заполнено'}
              </p>
              <Pencil />
            </button>
            {showErrors && missingSteps?.includes(step.key) ? (
              <p className='fig-field-error' role='alert' style={{ marginTop: -8 }}>
                Обязательный пункт - нажмите на поле, чтобы указать.
              </p>
            ) : null}
          </Fragment>
        ))}

        <p className={screen.mute}>Стоимость и длительность занятия согласуются с репетитором.</p>

        <div className={screen.actions}>
          <Button
            size='lg'
            shape='brick'
            variant='filled'
            color='primary'
            loading={addBulletin.isPending}
            onClick={async () => {
              if (incomplete) {
                setSaveAttempted(true);
                return;
              }
              setSubmitError(null);
              try {
                const payload = mapTaskFormToRequest(values, {
                  registrant_type: profileContext.registrant_type,
                  name: profile?.name,
                  surname: profile?.surname,
                });

                const preferredTutor = Number(values.preferredTutorProfileId || 0);
                const result = await addBulletin.mutateAsync(payload);
                reachMetrikaGoal(METRIKA_GOALS.taskCreated);

                const id = result?.id;
                reset();

                if (id && preferredTutor > 0) {
                  try {
                    const proposal = await createStudentProposal(id, {
                      tutor_profile_id: preferredTutor,
                      message: 'Здравствуйте! Хочу заниматься по выбранной услуге.',
                    });
                    if (proposal?.chat_id) {
                      navigate(
                        buildChatUrl({
                          chatId: proposal.chat_id,
                          roomId: proposal.room_id,
                          userId: proposal.tutor_id,
                          bulletinId: id,
                          role: 'student',
                        }),
                      );
                      return;
                    }
                  } catch {
                    addNotification(
                      'Заявка создана. Этого репетитора сейчас нельзя пригласить - выберите другого в списке.',
                      'info',
                    );
                  }
                }

                if (id) {
                  navigate(`/profile/details/task/${id}`);
                }
              } catch (error) {
                console.error('Ошибка создания задачи', error);
                const detail = (error as { response?: { data?: { detail?: unknown } } })?.response
                  ?.data?.detail;
                const text =
                  typeof detail === 'string' && detail.trim()
                    ? detail.trim()
                    : 'Не удалось сохранить задачу. Попробуйте еще раз.';
                setSubmitError(
                  /имени/i.test(text)
                    ? `${text} Имя берется из профиля. Чтобы его исправить, отправьте заявку на изменение данных в разделе «Настройки».`
                    : text,
                );
              }
            }}
          >
            Сохранить
          </Button>
          {showErrors ? (
            <p className='fig-field-error' role='alert'>
              Чтобы сохранить задачу, заполните: {missingTitles.join(', ')}.
            </p>
          ) : null}
          {submitError && !showErrors ? (
            <p className='fig-field-error' role='alert'>
              {submitError}
            </p>
          ) : null}
        </div>
      </section>

      {modalOpen ? (
        <TaskWizardModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          initialStep={currentIndex}
        />
      ) : null}
    </main>
  );
};
